// apolo/src/lib/loop/autoCompact.js — harness-live-verify E3 (auto-compact por tokens).
// @arch:pure-engine
//
// Decisão PURA de auto-compactação: antes do send, se o histórico MODEL-FACING
// (buildAgentHistory — que reseta no marker context_summary) cruza ~80% da janela do
// modelo, o gate manda inserir um compactMarker pelo MESMO caminho do /compact
// (determinístico, sem LLM). Guard anti-thrash: compactou 3x seguidas e a janela
// re-encheu ao limite em <3 turnos de usuário ⇒ PARA de auto-compactar e emite um
// system row honesto. Quem mede pct/insere marker é o caller (autoCompactGate.js);
// aqui só a máquina de estados — determinística e testável.

export const AUTO_COMPACT_PCT = 80;
export const THRASH_COMPACT_STREAK = 3;
export const THRASH_REFILL_TURNS = 3;

/** Estado do gate por sessão. `turnsAtCompact` = turnos de USUÁRIO no feed ao compactar. */
export function initialGateState() {
  return { compactCount: 0, turnsAtCompact: 0, msgsAtCompact: 0, lastCompactAt: 0, stopped: false };
}

/** Turnos de usuário no feed (clock monotônico — imune ao reset do marker). */
export function countUserTurns(messages) {
  let n = 0;
  for (const m of Array.isArray(messages) ? messages : []) if (m?.role === 'user') n += 1;
  return n;
}

/**
 * Avalia UM pré-send. Retorna a ação + o próximo estado (imutável).
 *   'none'    — abaixo do limiar / gate parado / pct desconhecido.
 *   'compact' — inserir o marker agora (estado grava o streak + o clock).
 *   'stop'    — thrash detectado (3 compactações seguidas e re-encheu em <3 turnos):
 *               transição única stopped:false→true; caller emite o row honesto.
 * @param {ReturnType<typeof initialGateState>} state
 * @param {{ pct:(number|null), userTurns:number, msgCount?:number, now?:number, threshold?:number }} obs
 */
export function evaluateAutoCompact(state, { pct, userTurns, msgCount = 0, now = 0, threshold = AUTO_COMPACT_PCT }) {
  const s = state || initialGateState();
  if (s.stopped || !Number.isFinite(pct) || pct < threshold) return { action: 'none', state: s };
  const fastRefill = s.compactCount > 0 && (userTurns - s.turnsAtCompact) < THRASH_REFILL_TURNS;
  if (s.compactCount >= THRASH_COMPACT_STREAK && fastRefill) {
    return { action: 'stop', state: { ...s, stopped: true } };
  }
  return {
    action: 'compact',
    state: {
      ...s,
      compactCount: fastRefill ? s.compactCount + 1 : 1,
      turnsAtCompact: userTurns,
      msgsAtCompact: msgCount,
      lastCompactAt: now,
    },
  };
}

/** System row honesto do anti-thrash (feed + persistido; 1x por sessão). */
export function thrashNoticeMessage() {
  return {
    role: 'system',
    content: '⚠️ O contexto re-encheu logo após compactar (3x seguidas) — a auto-compactação foi pausada nesta sessão. Divida a tarefa em partes menores ou abra uma sessão nova.',
  };
}
